import React, { useState } from 'react';
import StorePhoto from './StorePhoto';
import { groupOfType } from '../lib/productTypes';

// WO-20: product image with a drawn placeholder. Sealed products have no images yet (D9: no TCGplayer CDN hotlinking),
// so every sealed tile shows an outline of its kind (box, ETB, pack, tin, ...) tinted per kind. A real image, when a
// product gets one, replaces the drawing; a broken image falls back to it.
// WO-30: no source lets us use sealed product photos (docs/WO-30_VISUAL_PASS.md), so the drawing carries the set:
// the set's cover card (one of its own cards that we host, API `cover`) stands in front of the kind's outline, whole
// and with nothing drawn over it; without a cover, the set code is drawn in the outline.
// WO-34: a store listing's photo (API photo_url / photo_credit), when the product has one, comes before the mockup.

const TINTS = {
  box: '#8b7ff0', etb: '#BFD7FF', bundle: '#5DA271', pack: '#f0a35e', collection: '#e48bc4',
  tin: '#DDD8B8', deck: '#7fd4e0', case: '#a7a3c9',
};

function Shape({ art, c }) {
  const s = { fill: 'none', stroke: c, strokeWidth: 2.2, strokeLinejoin: 'round', strokeLinecap: 'round' };
  const f = { fill: c, fillOpacity: 0.12, stroke: c, strokeWidth: 2.2, strokeLinejoin: 'round' };
  switch (art) {
    case 'etb':   // tall box with a lid
      return (<g><rect x="32" y="30" width="56" height="64" rx="3" {...f} /><rect x="29" y="24" width="62" height="14" rx="3" {...s} />
        <path d="M48 58h24M48 68h16" {...s} /></g>);
    case 'pack':  // foil pack with crimped ends
      return (<g><path d="M40 26h40l-3 6 3 6v44l-3 6 3 6H40l3-6-3-6V38l3-6z" {...f} /><circle cx="60" cy="60" r="9" {...s} /></g>);
    case 'bundle': // stack of packs
      return (<g><rect x="30" y="38" width="30" height="48" rx="3" {...f} /><rect x="45" y="32" width="30" height="48" rx="3" {...f} />
        <rect x="60" y="26" width="30" height="48" rx="3" {...f} /></g>);
    case 'tin':
      return (<g><ellipse cx="60" cy="36" rx="28" ry="9" {...s} /><path d="M32 36v42c0 5 12.5 9 28 9s28-4 28-9V36" {...f} />
        <circle cx="60" cy="62" r="8" {...s} /></g>);
    case 'collection': // wide box with a window
      return (<g><rect x="22" y="34" width="76" height="54" rx="4" {...f} /><rect x="34" y="44" width="22" height="32" rx="2" {...s} />
        <path d="M64 50h22M64 60h22M64 70h14" {...s} /></g>);
    case 'deck': // card stack
      return (<g><rect x="42" y="30" width="38" height="54" rx="4" {...s} transform="rotate(-8 61 57)" />
        <rect x="42" y="30" width="38" height="54" rx="4" {...f} transform="rotate(6 61 57)" /></g>);
    case 'case': // crate of boxes
      return (<g><rect x="24" y="30" width="72" height="58" rx="3" {...f} /><path d="M24 50h72M24 70h72M48 30v58M72 30v58" {...s} /></g>);
    default:      // booster box, 3/4 view
      return (<g><path d="M28 44l32-14 32 14v36L60 94 28 80z" {...f} /><path d="M28 44l32 14 32-14M60 58v36" {...s} /></g>);
  }
}

// WO-34 photoAt moved to lib/storePhotos.js (WO-35); re-exported here for existing imports.
export { photoAt } from '../lib/storePhotos';

export default function SealedArt({ productType, typeName, image = null, alt = '', className = '', showLabel = true,
                                    cover = null, setCode = null, coverSize = 'small', art = null,
                                    photo = null, photoCredit = null, onPhotoBroken = null, photoHints = null }) {
  const [broken, setBroken] = useState(false);
  const [coverBroken, setCoverBroken] = useState(false);
  const [artBroken, setArtBroken] = useState(false);
  const [photoBroken, setPhotoBroken] = useState(false);
  const group = groupOfType(productType, typeName);
  // WO-34: a real photo from a store listing (credited); any load error falls back to the mockup below.
  if (photo && !photoBroken && !(image && !broken)) {
    // WO-35: the shared store-photo frame (cream stage, white melts in); the product page prints its credit below.
    const large = coverSize === 'large';
    return (
      <div className={`relative flex items-center justify-center bg-[#1A1A20] ${className}`}>
        <StorePhoto url={photo} alt={alt} credit={photoCredit} variant={large ? 'hero' : 'tile'} hints={photoHints}
                    creditMode={large ? 'none' : 'overlay'} testId="sealed-photo"
                    onError={() => { setPhotoBroken(true); if (onPhotoBroken) onPhotoBroken(); }} />
      </div>
    );
  }
  if (image && !broken) {
    return (
      <div className={`flex items-center justify-center bg-dark ${className}`}>
        <img src={image} alt={alt} loading="lazy" onError={() => setBroken(true)} className="object-contain w-full h-full" />
      </div>
    );
  }
  // WO-30 follow-up: server-drawn mockup (the kind of product with its set's cards on the front); an illustration,
  // not a photo of the product. The drawing below stays as the fallback.
  if (art?.small && !artBroken) {
    return (
      <div className={`relative flex items-center justify-center bg-[#16161A] overflow-hidden ${className}`} data-testid="sealed-art-generated">
        <img src={coverSize === 'large' && art.large ? art.large : art.small}
             srcSet={coverSize === 'large' ? undefined : `${art.small} 400w, ${art.large} 800w`}
             sizes={coverSize === 'large' ? undefined : '(max-width: 640px) 50vw, 260px'}
             alt={alt ? `${alt} (illustration)` : 'Illustration'} loading="lazy" onError={() => setArtBroken(true)}
             className="object-contain w-full h-full" />
      </div>
    );
  }
  const c = TINTS[group.art] || TINTS.box;
  const withCover = cover?.image_small && !coverBroken;
  const code = setCode ? (/^\d+$/.test(setCode) ? `SET ${setCode}` : String(setCode).toUpperCase()) : null;   // Lorcana codes are numbers
  return (
    <div className={`relative flex flex-col items-center justify-center overflow-hidden ${className}`}
         style={{ background: `radial-gradient(circle at 50% 40%, ${c}22, #16161A 70%)` }}
         role="img" aria-label={alt || typeName || 'Sealed product'}
         data-testid={withCover ? 'sealed-art-cover' : 'sealed-art'}>
      {withCover ? (
        // the kind's outline (what the product is) with the set's card in front of its right side (which set)
        // sized by width so it fits every tile shape (4:3 grid, 16:10 strip, square product page)
        <div className="flex items-center justify-center w-full h-full">
          <svg viewBox="0 0 120 120" className="w-[46%] h-auto shrink-0 -mr-[5%]" aria-hidden="true"><Shape art={group.art} c={c} /></svg>
          <img src={coverSize === 'large' && cover.image_large ? cover.image_large : cover.image_small} alt="" loading="lazy"
               onError={() => setCoverBroken(true)}
               className="relative w-[29%] h-auto object-contain rounded-[4%] translate-y-[8%] shadow-[0_8px_22px_rgba(0,0,0,0.6)]" />
        </div>
      ) : (
        <svg viewBox="0 0 120 120" className={code ? 'w-1/2 h-1/2' : 'w-3/5 h-3/5'} aria-hidden="true"><Shape art={group.art} c={c} /></svg>
      )}
      {code && withCover && (
        <span className={`absolute left-2 ${showLabel ? 'top-2' : 'bottom-2'} rounded bg-dark/80 border px-1.5 py-0.5 font-mono text-[10px] tracking-widest text-cream/80`}
              style={{ borderColor: `${c}66` }}>
          {code}
        </span>
      )}
      {code && !withCover && (
        <span className="mt-1 font-mono font-semibold tracking-widest text-sm sm:text-base" style={{ color: c }}>{code}</span>
      )}
      {showLabel && typeName && (
        <span className="absolute bottom-2 left-0 right-0 text-center text-[10px] uppercase tracking-widest text-cream/40 px-2 truncate">{typeName}</span>
      )}
    </div>
  );
}
